import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";
import { useContext, useEffect, useState } from "react";
import {
  SHIPMENT_STATE_LABELS,
  ShipmentState,
  ShipmentType,
} from "types/ShippingTypes";
import { apiGetShipments } from "util/network/Shipping";
import ShipmentsTable from "./ShipmentsTable";
import { ShipmentPage, errorText } from "./common";

const LIMIT = 200;

/** Every shipment, newest first: labels, pickups and own deliveries. */
const ShipmentsList: React.FC = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [state, setState] = useStateWithLocalStorage<ShipmentState | "">(
    "",
    "shipmentsListState"
  );
  const [shipments, setShipments] = useState<ShipmentType[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    apiGetShipments(token, { state: state || undefined, limit: LIMIT })
      .then(setShipments)
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) load();
  }, [token, state]);

  const patch = (updated: ShipmentType) =>
    setShipments((rows) =>
      rows.map((row) => (row.id === updated.id ? updated : row))
    );

  return (
    <ShipmentPage
      title="Shipments"
      actions={
        <Tooltip title="Refresh">
          <IconButton onClick={load} sx={{ color: "#fff" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>State</InputLabel>
          <Select
            label="State"
            value={state}
            onChange={(event) =>
              setState(event.target.value as ShipmentState | "")
            }
          >
            <MenuItem value="">All</MenuItem>
            {(Object.keys(SHIPMENT_STATE_LABELS) as ShipmentState[]).map(
              (value) => (
                <MenuItem key={value} value={value}>
                  {SHIPMENT_STATE_LABELS[value]}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <ShipmentsTable
          shipments={shipments}
          showOrder
          onChanged={patch}
          onRebooked={(old, created) =>
            setShipments((rows) => [
              created,
              ...rows.map((row) =>
                row.id === old.id
                  ? { ...row, state: "cancelled" as const, canCancel: false }
                  : row
              ),
            ])
          }
        />
      )}
    </ShipmentPage>
  );
};

export default ShipmentsList;
